import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { CheckboxInput } from '@adornis/formfield/fields/checkbox-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { getPossibleKlassenForSchoolType, klasseLabel } from '../../../groups/api/getPossibleKlassenForSchoolType.js';
import { ActivatedRegisterType, type School } from '../../../groups/entities/School.js';
import { Person } from '../../../users/entities/person.js';
import { inActiveUI, isSusEntryFormActive } from '../../isSusEntryFormActive.js';
import { engineFieldAttributes } from '../../registration-form/client/fields/engine-field-attributes.js';
import { RegistrationSteps } from '../../registration-steps.js';
import { SchoolRegisterOrigin } from '../../registrationMode.js';
import { SuSInitalRegistrationData, sendSuSInitalSchoolSelectedMail } from '../api/sendSuSInitalSchoolSelectedMail.js';

type RegistrationSchool = NarrowedEntity<School, { _id: 1; name: 1; schoolType: 1; mode: 1 }>;
const SchoolRegistration = stateful((props: { school: RegistrationSchool; close: () => void }) => {
  const schoolMode = props.school.mode ?? ActivatedRegisterType.ALL;
  const classes = getPossibleKlassenForSchoolType(props.school.schoolType, schoolMode as ActivatedRegisterType);
  const user = useObservable(() => currentUser<Person>(), []);
  const [userEmail, setUserEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [klasse, setKlasse] = useState(classes[0] ?? '');
  const [origin, setOrigin] = useState(
    schoolMode === ActivatedRegisterType.SCHOOL_SCHOOL
      ? SchoolRegisterOrigin.CHANGE_SCHOOL
      : SchoolRegisterOrigin.ELEMENTARY_HIGH,
  );
  const [childID, setChildID] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const { value: active, loading, error: activeError } = useQuery(() => isSusEntryFormActive()(), []);
  const {
    value: children,
    loading: childrenLoading,
    error: childrenError,
  } = useQuery(
    () =>
      user?.sorgeberechtigteKinderIDs?.length
        ? getByID(
            Person,
            user.sorgeberechtigteKinderIDs,
          )({ _id: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } })
        : Promise.resolve([]),
    [user?._id],
  );
  useEffect(() => {
    if (user?.email) setUserEmail(user.email);
  }, [user?._id]);

  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    if (sending || sent) return;
    if (!klasse || !acceptedTerms || !acceptedPrivacy) {
      setError('Bitte füllen Sie alle Pflichtfelder aus und bestätigen Sie beide Einverständnisse.');
      return;
    }
    const email = String(new FormData(event.currentTarget as HTMLFormElement).get('email') ?? '').trim();
    setUserEmail(email);
    setSending(true);
    setError('');
    try {
      await sendSuSInitalSchoolSelectedMail(
        new SuSInitalRegistrationData({
          email,
          schoolID: props.school._id,
          klasse,
          childID: childID || null,
          origin,
        }),
      )();
      setSent(true);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Die E-Mail konnte nicht versendet werden. Bitte versuchen Sie es erneut.',
      );
    } finally {
      setSending(false);
    }
  };

  return html`<div class="flex min-w-0 flex-col gap-5 p-4 sm:p-6">
    <header class="flex flex-col gap-4">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h2 class="mt-1 font-semibold break-words md:text-lg">${props.school.name}</h2>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-square shrink-0"
          aria-label="Dialog schließen"
          ?disabled=${sending}
          @click=${props.close}
        >
          <span class="icon-[material-symbols--close-rounded] text-xl" aria-hidden="true"></span>
        </button>
      </div>
      ${RegistrationSteps(2)}
    </header>
    ${
      sent
        ? html`<section role="status" class="flex flex-col gap-4">
            <span
              class="icon-[material-symbols--mark-email-read-rounded] text-4xl text-primary"
              aria-hidden="true"
            ></span>
            <h3 class="font-semibold">Ihre Einladung ist unterwegs</h3>
            <p>
              Wir haben den Link zum Bewerbungsbogen an <strong class="break-all">${userEmail}</strong> gesendet. Der
              Link ist 21 Tage gültig.
            </p>
            <p>Bitte prüfen Sie auch Ihren Spamordner.</p>
            <button type="button" class="btn btn-primary w-full" @click=${props.close}>Zur Schulauswahl</button>
          </section>`
        : loading
          ? html`<p role="status">
              <span class="loading loading-spinner" aria-hidden="true"></span> Anmeldung wird geladen …
            </p>`
          : activeError
            ? html`<p role="alert" class="alert alert-error alert-soft text-base-content">
                Die Anmeldung konnte nicht geladen werden. Bitte schließen Sie den Dialog und versuchen Sie es erneut.
              </p>`
            : !active
              ? inActiveUI
              : html` <p>
                    Wir senden Ihnen den persönlichen Link zum Bewerbungsbogen. Ihre Bewerbung können Sie anschließend
                    in Ruhe ausfüllen.
                  </p>
                  ${!classes.length ? html`<p role="alert" class="alert alert-warning alert-soft text-base-content">Für diese Schule sind noch keine Klassenstufen hinterlegt. Bitte wenden Sie sich an das Sekretariat.</p>` : nothing}
                  <form class="flex flex-col gap-5" @submit=${submit}>
                    <fieldset
                      class="flex min-w-0 flex-col gap-5 [&_.label]:items-start [&_.label]:whitespace-normal [&_.label]:text-base-content [&_.label]:text-sm [&_.checkbox]:shrink-0"
                      ?disabled=${sending}
                    >
                      <div
                        class="flex min-w-0 flex-col gap-2 [&_input]:text-base"
                        ${engineFieldAttributes({ id: 'sus-invitation-email', label: 'Ihre E-Mail-Adresse', name: 'email', required: true })}
                      >
                        <label class="text-sm font-semibold text-base-content/70" for="sus-invitation-email"
                          >Ihre E-Mail-Adresse *</label
                        >
                        ${TextInput({ ariaLabel: 'Ihre E-Mail-Adresse', type: 'email', autocomplete: 'email', value: userEmail, disabled: sending, onPicked: value => setUserEmail(value ?? '') })}
                      </div>
                      <div
                        class="flex min-w-0 flex-col gap-2 [&_input]:text-base"
                        ${engineFieldAttributes({ id: 'sus-invitation-class', label: 'Gewünschte Klassenstufe', name: 'class', required: true })}
                      >
                        <label class="text-sm font-semibold text-base-content/70" for="sus-invitation-class"
                          >Gewünschte Klassenstufe *</label
                        >
                        ${DropdownSelectionInput({ ariaLabel: 'Gewünschte Klassenstufe', value: klasse, selectables: classes, searchable: false, disabled: sending, onPicked: value => setKlasse(value ?? ''), renderString: klasseLabel })}
                      </div>
                      ${
                        klasse === '5' && schoolMode === ActivatedRegisterType.ALL
                          ? html` <div
                              class="flex min-w-0 flex-col gap-2 [&_input]:text-base"
                              ${engineFieldAttributes({ id: 'sus-invitation-origin', label: 'Grund der Anmeldung', name: 'origin', required: false })}
                            >
                              <label class="text-sm font-semibold text-base-content/70" for="sus-invitation-origin"
                                >Grund der Anmeldung</label
                              >
                              ${DropdownSelectionInput({ ariaLabel: 'Grund der Anmeldung', value: origin, selectables: Object.values(SchoolRegisterOrigin), searchable: false, disabled: sending, onPicked: value => value && setOrigin(value) })}
                            </div>`
                          : nothing
                      }
                      ${
                        user && childrenLoading
                          ? html`<p role="status">Kinder werden geladen …</p>`
                          : childrenError
                            ? html`<p role="alert" class="alert alert-warning alert-soft text-base-content">
                                Bestehende Kinderdaten konnten nicht geladen werden. Bitte versuchen Sie es später
                                erneut, wenn Sie ein bereits erfasstes Kind bewerben möchten.
                              </p>`
                            : children?.length
                              ? html` <div
                                  class="flex min-w-0 flex-col gap-2 [&_input]:text-base"
                                  ${engineFieldAttributes({ id: 'sus-invitation-child', label: 'Für welches Kind?', name: 'child', required: false })}
                                >
                                  <label class="text-sm font-semibold text-base-content/70" for="sus-invitation-child"
                                    >Für welches Kind?</label
                                  >
                                  ${DropdownSelectionInput({
                                    ariaLabel: 'Für welches Kind?',
                                    value: childID || 'new',
                                    selectables: ['new', ...children.map(child => child._id)],
                                    searchable: false,
                                    disabled: sending,
                                    onPicked: value => setChildID(value === 'new' ? '' : (value ?? '')),
                                    renderString: value => {
                                      const child = children.find(child => child._id === value);
                                      return child
                                        ? `${child.profilDaten?.stammdaten?.vorname ?? ''} ${child.profilDaten?.stammdaten?.nachname ?? ''}`
                                        : 'Ein neues Kind bewerben';
                                    },
                                  })}
                                </div>`
                              : nothing
                      }
                      <div
                        ${engineFieldAttributes({ id: 'sus-consent', label: 'Einverständnis aller Sorgeberechtigten', required: true })}
                      >
                        ${CheckboxInput({ value: acceptedTerms, disabled: sending, onPicked: value => setAcceptedTerms(!!value), label: 'Das Ausfüllen des Bewerbungsbogens erfolgt im Einverständnis aller Sorgeberechtigten. Im Bewerbungsgespräch müssen alle Sorgeberechtigten unterschreiben oder ihr schriftliches Einverständnis vorlegen. *' })}
                      </div>
                      <div
                        class="flex flex-col gap-2"
                        ${engineFieldAttributes({ id: 'sus-privacy', label: 'Datenschutzbestimmungen akzeptieren', required: true })}
                      >
                        ${CheckboxInput({ value: acceptedPrivacy, disabled: sending, onPicked: value => setAcceptedPrivacy(!!value), label: 'Wir haben die Datenschutzbestimmungen gelesen und akzeptiert. *' })}
                        <a
                          href="/datenschutz"
                          target="_blank"
                          rel="noopener"
                          class="link inline-flex min-h-11 items-center"
                          >Datenschutzbestimmungen lesen</a
                        >
                      </div>
                    </fieldset>
                    ${error ? html`<p class="alert alert-error alert-soft text-base-content" role="alert">${error}</p>` : nothing}
                    <button
                      class="btn btn-primary min-h-11 w-full"
                      type="submit"
                      ?disabled=${sending || !classes.length}
                    >
                      ${sending ? html`<span class="loading loading-spinner loading-sm" aria-hidden="true"></span>Wird gesendet …` : 'Einladungslink anfordern'}
                    </button>
                    <p class="text-sm">
                      * Pflichtfelder. Hiermit fordern Sie den Bewerbungsbogen an; die Bewerbung wird erst am Ende
                      dieses Bogens abgesendet.
                    </p>
                  </form>`
    }
  </div>`;
});

export async function SgSusEntryFormSchoolRegistrationDialog(school: RegistrationSchool, userLoggedIn?: boolean) {
  if (!userLoggedIn) {
    const choice = await showDialog<'login' | 'guest' | null>({
      modalBoxClasses: 'w-full max-w-xl max-h-[calc(100dvh-1rem)]',
      content: ok =>
        html`<div class="flex flex-col gap-5 p-4 sm:p-6">
          <header class="flex items-start justify-between gap-3">
            <h2 class="font-semibold md:text-lg">Bewerbung starten</h2>
            <button
              type="button"
              class="btn btn-ghost btn-square shrink-0"
              aria-label="Dialog schließen"
              @click=${() => ok(null)}
            >
              <span class="icon-[material-symbols--close-rounded] text-xl" aria-hidden="true"></span>
            </button>
          </header>
          <p>
            Haben Sie bereits einen Zugang bei der Martinus-SG? Melden Sie sich an, damit vorhandene Daten übernommen
            werden können.
          </p>
          <div class="flex flex-col gap-3">
            <button type="button" class="btn btn-primary min-h-11 w-full" @click=${() => ok('login')}>
              Mit bestehendem Zugang anmelden</button
            ><button type="button" class="btn btn-outline min-h-11 w-full" @click=${() => ok('guest')}>
              Ohne Zugang fortfahren
            </button>
          </div>
        </div>`,
    }).catch(() => null);
    if (!choice) return;
    if (choice === 'login') {
      goTo(`/login/sus-schulen?schoolID=${encodeURIComponent(school._id)}`);
      return;
    }
  }
  await showDialog<null>({
    modalBoxClasses: 'w-full max-w-xl max-h-[calc(100dvh-1rem)]',
    content: ok => SchoolRegistration({ school, close: () => ok(null) }),
  }).catch(() => null);
}
